import React, { useCallback, useEffect, useState } from 'react';
import { supportingDataService, SupportingDataItem } from '../../../../services/supportingDataService';
import { CtaForm, CtaEntry, CtaRegistry, EMPTY_CTA, THEME_OPTIONS } from './CtaForm';

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------

export function CtaRegistryEditor({ item, onBack }: {
  item: SupportingDataItem;
  onBack: () => void;
}) {
  const [registry, setRegistry] = useState<CtaRegistry | null>(null);
  const [originalJson, setOriginalJson] = useState('');
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await supportingDataService.get(item.asset_id);
        const parsed = JSON.parse(data.content) as CtaRegistry;
        setRegistry(parsed);
        setOriginalJson(JSON.stringify(parsed));
        setEtag(data.etag);
      } catch (e: any) {
        setError(e?.message || 'Failed to load CTA registry');
      } finally {
        setLoading(false);
      }
    })();
  }, [item.asset_id]);

  const isDirty = registry ? JSON.stringify(registry) !== originalJson : false;

  const handleSave = useCallback(async () => {
    if (!registry) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const content = JSON.stringify(registry, null, 2);
      const result = await supportingDataService.update(item.asset_id, content, etag);
      setEtag(result.etag);
      setOriginalJson(JSON.stringify(registry));
      setSavedMessage('Saved successfully');
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [registry, etag, item.asset_id]);

  const handleDiscard = () => {
    if (originalJson) {
      setRegistry(JSON.parse(originalJson));
      setExpandedId(null);
    }
    setError(null);
    setSavedMessage(null);
  };

  const updateCta = (index: number, updated: CtaEntry) => {
    if (!registry) return;
    const next = { ...registry, ctas: [...registry.ctas] };
    next.ctas[index] = updated;
    setRegistry(next);
  };

  const deleteCta = (index: number) => {
    if (!registry) return;
    const next = { ...registry, ctas: registry.ctas.filter((_, i) => i !== index) };
    setRegistry(next);
    setExpandedId(null);
  };

  const addCta = () => {
    if (!registry) return;
    const newId = `new-cta-${Date.now()}`;
    const newCta = { ...EMPTY_CTA, id: newId };
    setRegistry({ ...registry, ctas: [...registry.ctas, newCta] });
    setExpandedId(newId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!registry) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error || 'Failed to load'}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-3 inline-block">
          &larr; Back to supporting data
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{item.name}</h2>
            <p className="text-sm text-gray-500 mt-1">{item.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600">Default Theme</label>
            <select
              value={registry.default_theme}
              onChange={(e) => setRegistry({ ...registry, default_theme: e.target.value })}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {THEME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* CTA list */}
      <div className="space-y-3">
        {registry.ctas.map((cta, index) => {
          const isExpanded = expandedId === cta.id;

          if (isExpanded) {
            return (
              <CtaForm
                key={cta.id}
                cta={cta}
                onChange={(updated) => updateCta(index, updated)}
                onDelete={() => deleteCta(index)}
                isNew={cta.id.startsWith('new-cta-')}
              />
            );
          }

          return (
            <div
              key={cta.id}
              className="bg-white border rounded p-3 cursor-pointer hover:bg-gray-50 transition-all"
              style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4'; }}
              onClick={() => setExpandedId(cta.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">{cta.id}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{cta.audience}</span>
                    {cta.funnel_stages?.map(s => (
                      <span key={s} className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{s}</span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-500 mt-0.5 truncate max-w-xl">{cta.headline}</div>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add button */}
      <button
        onClick={addCta}
        className="w-full py-2 text-sm font-medium text-gray-600 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        + Add CTA
      </button>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleDiscard}
          disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Discard changes
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      {/* Warning */}
      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Changes affect all future article generation runs immediately.</span>
      </div>
    </div>
  );
}
